import { useEscClose } from '../hooks/useEscClose';
// CourseScriptDownloadModal — Progress-Modal for background PDF generation.
//
// UX flow:
// 1. User clicks the "Kurs-Skript herunterladen" button in CourseView.
// 2. Modal opens showing "Wird vorbereitet …".
// 3. POST /api/user/course-script/{course_id}/start returns a job_id.
// 4. Poll GET /api/user/course-script/status/{job_id} every 500 ms and
//    render a progress bar with the current module title.
// 5. When status === "done", trigger a hidden anchor click on
//    /api/user/course-script/download/{job_id} to download the PDF.

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Download, Loader2, CheckCircle2, AlertCircle, BookOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const CourseScriptDownloadModal = ({ courseId, courseTitle, getAuthHeader, onClose }) => {
  const [status, setStatus] = useState("starting");   // starting|loading_content|rendering_pdf|done|error
  const [currentModule, setCurrentModule] = useState(0);
  const [totalModules, setTotalModules] = useState(0);
  const [currentTitle, setCurrentTitle] = useState("");
  const [progressPct, setProgressPct] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [sizeBytes, setSizeBytes] = useState(0);
  const [filename, setFilename] = useState("Kursskript.pdf");
  const [errorMsg, setErrorMsg] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const pollingRef = useRef(null);
  const jobIdRef = useRef(null);
  const startedRef = useRef(false);
  const consecutiveErrorsRef = useRef(0);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startJob = async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    try {
      const res = await axios.post(
        `${API}/user/course-script/${courseId}/start`,
        {},
        getAuthHeader(),
      );
      jobIdRef.current = res.data.job_id;
      // Poll every 500 ms
      pollingRef.current = setInterval(pollStatus, 500);
    } catch (e) {
      setStatus("error");
      setErrorMsg(e.response?.data?.detail || "Konnte Job nicht starten");
    }
  };

  const pollStatus = async () => {
    if (!jobIdRef.current) return;
    try {
      const res = await axios.get(
        `${API}/user/course-script/status/${jobIdRef.current}`,
        getAuthHeader(),
      );
      consecutiveErrorsRef.current = 0;
      const d = res.data;
      setStatus(d.status);
      setCurrentModule(d.current_module || 0);
      setTotalModules(d.total_modules || 0);
      setCurrentTitle(d.current_module_title || "");
      setProgressPct(d.progress_percent || 0);
      setElapsed(d.elapsed_seconds || 0);
      setSizeBytes(d.size_bytes || 0);
      setFilename(d.filename || "Kursskript.pdf");
      if (d.status === "done" || d.status === "error") {
        stopPolling();
        if (d.status === "error") setErrorMsg(d.error);
      }
    } catch (e) {
      // Count consecutive polling failures; abort after 10 (≈ 5 seconds)
      // so the modal doesn't hang forever on network issues.
      consecutiveErrorsRef.current += 1;
      if (consecutiveErrorsRef.current >= 10) {
        stopPolling();
        setStatus("error");
        setErrorMsg(
          e.response?.data?.detail ||
          "Verbindung zum Server verloren. Bitte später erneut versuchen."
        );
      }
    }
  };

  const triggerDownload = async () => {
    if (!jobIdRef.current) return;
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/user/course-script/download/${jobIdRef.current}`,
        { ...getAuthHeader(), responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", filename);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Kursskript heruntergeladen!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Download");
    }
    setDownloading(false);
  };

  useEffect(() => {
    startJob();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFinal = status === "done" || status === "error";
  useEscClose(() => { if (isFinal) onClose(); });
  const label =
    status === "starting" ? "Wird gestartet…" :
    status === "loading_content" ? "Lade Modulinhalte…" :
    status === "rendering_pdf" ? "Layout & Rendering…" :
    status === "done" ? "Fertig!" :
    status === "error" ? "Fehler" : "Wird verarbeitet…";

  const sizeKB = sizeBytes > 0 ? (sizeBytes / 1024).toFixed(0) : null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-espresso/50 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Kurs-Skript herunterladen"
      data-testid="course-script-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && isFinal) onClose();
      }}
    >
      <div className="bg-cream-100 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="p-6 border-b border-cream-200 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-semibold text-terra mb-1">
              Dickies-Campus Academy Magazine
            </div>
            <h3 className="font-heading text-xl font-semibold text-sand-800 leading-snug">
              Kurs-Skript herunterladen
            </h3>
            <p className="text-sm text-sand-600 mt-1 line-clamp-1">{courseTitle}</p>
          </div>
          {isFinal && (
            <button
              onClick={onClose}
              className="text-sand-500 hover:text-sand-800 transition-colors p-1"
              data-testid="close-modal"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* State icon */}
          <div className="flex items-center justify-center py-4">
            {status === "done" ? (
              <div className="w-16 h-16 rounded-full bg-sage/15 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-sage-dark" strokeWidth={2} />
              </div>
            ) : status === "error" ? (
              <div className="w-16 h-16 rounded-full bg-coral-100 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-coral-dark" strokeWidth={2} />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-terra/10 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-terra animate-spin" strokeWidth={2} />
              </div>
            )}
          </div>

          {/* Label */}
          <div className="text-center">
            <div className="font-heading text-base font-semibold text-sand-800">{label}</div>
            {totalModules > 0 && !isFinal && (
              <div className="text-xs text-sand-500 uppercase tracking-wider mt-1 font-medium">
                Modul {currentModule} von {totalModules}
              </div>
            )}
          </div>

          {/* Progress bar */}
          {status !== "error" && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-sand-600 uppercase tracking-wider">
                  Fortschritt
                </span>
                <span className="text-xs font-mono font-semibold text-terra">
                  {progressPct}%
                </span>
              </div>
              <div className="h-2 bg-cream-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${status === "done" ? "bg-sage" : "bg-gradient-to-r from-terra to-terra-dark"}`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {/* Current module title */}
          {!isFinal && currentTitle && (
            <div className="p-3 rounded-xl bg-cream-100 border border-cream-200 flex items-start gap-2.5">
              <BookOpen className="w-4 h-4 text-terra flex-shrink-0 mt-0.5" />
              <div className="text-sm text-sand-700 line-clamp-2 leading-snug">
                {currentTitle}
              </div>
            </div>
          )}

          {/* Success meta */}
          {status === "done" && (
            <div className="p-4 rounded-xl bg-sage/8 border border-sage/30 space-y-1">
              <div className="text-xs uppercase tracking-wider font-semibold text-sage-dark">
                Bereit zum Download
              </div>
              <div className="text-sm text-sand-700">
                {totalModules} Module · {sizeKB} KB · in {elapsed}s erzeugt
              </div>
            </div>
          )}

          {/* Error */}
          {status === "error" && (
            <div className="p-4 rounded-xl bg-coral-50 border border-coral-200 text-sm text-coral-dark">
              {errorMsg || "Unbekannter Fehler bei der PDF-Erzeugung."}
            </div>
          )}
        </div>

        {/* Footer / actions */}
        <div className="p-6 border-t border-cream-200 flex justify-end gap-2">
          {status === "done" ? (
            <>
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-full bg-cream-100 border border-cream-300 text-sand-800 hover:bg-cream-100 font-semibold text-sm transition-colors"
              >
                Schließen
              </button>
              <button
                onClick={triggerDownload}
                disabled={downloading}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-terra hover:bg-terra-dark text-white font-semibold text-sm shadow-soft hover:shadow-hover transition-colors disabled:opacity-60"
                data-testid="download-course-script-final"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {downloading ? "Lade herunter…" : "Als PDF herunterladen"}
              </button>
            </>
          ) : status === "error" ? (
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-full bg-terra hover:bg-terra-dark text-white font-semibold text-sm transition-colors"
            >
              Schließen
            </button>
          ) : (
            <div className="text-xs text-sand-500 font-medium">
              Bitte warte einen Moment · das Dokument wird individuell für dich erzeugt.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseScriptDownloadModal;
